/**
 * emailIntentChannel — the chat lane's which-inbox handshake (C3b, 2026-08-21).
 *
 * The Integrations panel asks "which inbox should Prest0n connect?" in a
 * modal BEFORE the OAuth mint (C3a, EmailIntentPrompt). The chat connect
 * card needs the same beat, but its buttons live in raw
 * workflowActionButtonsHtml — no React, and an inline <input> would fight
 * the chat composer's global key handling. So the click handler asks over a
 * window CustomEvent round-trip and a host component (EmailIntentPromptHost,
 * mounted app-wide like the Composio coach host) renders the SAME modal.
 *
 * Protocol (requestId ties every leg to its request):
 *   requester → `prest0n:email-intent-request`  {requestId, toolkitSlug, displayName}
 *   host      → `prest0n:email-intent-ack`      {requestId}          (immediately)
 *   host      → `prest0n:email-intent-response` {requestId, intendedAddress|null, cancelled}
 *
 * No ack within ACK_TIMEOUT_MS means no host is mounted — resolve with no
 * intent so the connect proceeds exactly as before (graceful degrade, never
 * a dead click). After an ack, the wait is for USER input: unbounded except
 * a generous safety ceiling.
 */

export const EMAIL_INTENT_REQUEST_EVENT = 'prest0n:email-intent-request';
export const EMAIL_INTENT_ACK_EVENT = 'prest0n:email-intent-ack';
export const EMAIL_INTENT_RESPONSE_EVENT = 'prest0n:email-intent-response';

export interface EmailIntentRequestDetail {
  requestId: number;
  toolkitSlug: string;
  displayName: string;
}

export interface EmailIntentResponseDetail {
  requestId: number;
  intendedAddress: string | null;
  cancelled: boolean;
}

export interface EmailIntentResult {
  intendedAddress: string | null;
  cancelled: boolean;
}

const ACK_TIMEOUT_MS = 1500;
const RESPONSE_CEILING_MS = 5 * 60 * 1000;

let requestCounter = 0;

/** Test hook: deterministic ids across suites. */
export function resetEmailIntentRequestCounterForTests(): void {
  requestCounter = 0;
}

export function requestEmailIntent(
  toolkitSlug: string,
  displayName: string,
  timeouts?: { ackMs?: number; ceilingMs?: number },
): Promise<EmailIntentResult> {
  if (typeof window === 'undefined') {
    return Promise.resolve({ intendedAddress: null, cancelled: false });
  }
  const requestId = ++requestCounter;
  return new Promise<EmailIntentResult>((resolve) => {
    let settled = false;
    let acked = false;
    let ackTimer: ReturnType<typeof setTimeout> | null = null;
    let ceilingTimer: ReturnType<typeof setTimeout> | null = null;

    const finish = (result: EmailIntentResult) => {
      if (settled) return;
      settled = true;
      if (ackTimer) clearTimeout(ackTimer);
      if (ceilingTimer) clearTimeout(ceilingTimer);
      window.removeEventListener(EMAIL_INTENT_ACK_EVENT, onAck as EventListener);
      window.removeEventListener(EMAIL_INTENT_RESPONSE_EVENT, onResponse as EventListener);
      resolve(result);
    };

    const onAck = (e: Event) => {
      const detail = (e as CustomEvent).detail as { requestId?: number } | undefined;
      if (detail?.requestId !== requestId) return;
      acked = true;
      if (ackTimer) clearTimeout(ackTimer);
    };
    const onResponse = (e: Event) => {
      const detail = (e as CustomEvent).detail as EmailIntentResponseDetail | undefined;
      if (detail?.requestId !== requestId) return;
      finish({
        intendedAddress: detail.intendedAddress || null,
        cancelled: detail.cancelled === true,
      });
    };

    window.addEventListener(EMAIL_INTENT_ACK_EVENT, onAck as EventListener);
    window.addEventListener(EMAIL_INTENT_RESPONSE_EVENT, onResponse as EventListener);

    ackTimer = setTimeout(() => {
      // No host mounted — proceed with no intent, exactly the pre-C3b flow.
      if (!acked) finish({ intendedAddress: null, cancelled: false });
    }, timeouts?.ackMs ?? ACK_TIMEOUT_MS);
    ceilingTimer = setTimeout(() => {
      // Safety ceiling: a host that acked but never responded (unmounted
      // mid-prompt) must not wedge the click forever.
      finish({ intendedAddress: null, cancelled: true });
    }, timeouts?.ceilingMs ?? RESPONSE_CEILING_MS);

    window.dispatchEvent(new CustomEvent(EMAIL_INTENT_REQUEST_EVENT, {
      detail: {
        requestId,
        toolkitSlug: String(toolkitSlug || '').toLowerCase().slice(0, 60),
        displayName: String(displayName || toolkitSlug || '').slice(0, 80),
      } satisfies EmailIntentRequestDetail,
    }));
  });
}

/** Host side: acknowledge receipt so the requester knows a modal is coming. */
export function ackEmailIntentRequest(requestId: number): void {
  window.dispatchEvent(new CustomEvent(EMAIL_INTENT_ACK_EVENT, { detail: { requestId } }));
}

/** Host side: deliver the user's answer (or cancellation). */
export function respondEmailIntent(detail: EmailIntentResponseDetail): void {
  window.dispatchEvent(new CustomEvent(EMAIL_INTENT_RESPONSE_EVENT, { detail }));
}
